/**
 * The user wire shapes — `UserSummary`, `User`, `UserUpdate`, `SessionInfo` and
 * `MemberOverviewRow` from `docs/api/paths/users.yaml` / `platform.yaml` — and the row the
 * pages and forms render. Pure: safe to import from client components.
 */

import type { AccountTypeValue, ApiUserOwner, Balancesheet } from '@/lib/api/ledger/types'
import { strEnum } from '@/lib/api/types'
import type { ResumesType } from '@/types/resume'
import { z } from 'zod'

export const UserRolesType = strEnum(['member', 'accountant', 'admin', 'aspirant'])
export type UserRolesTypeValue = keyof typeof UserRolesType

/** `UserProfile.gender`; the contract writes the values inline. */
export const UserGenderType = strEnum(['men', 'women', 'other'])
export type UserGenderTypeValue = keyof typeof UserGenderType

/** The JSONB fields of a user (`UserProfile`, `SalaryConfiguration`, `UserContract`), as the forms validate them. */
export const UserProfileAttributesSchema = {
  address: z.string().optional(),
  telegram: z.string().optional(),
  linkedin: z.string().optional(),
  webpage: z.string().optional(),
  github: z.string().optional(),
  openForWork: z.string().optional(),
  iban: z.string().optional(),
  birthday: z
    .transform((_) => {
      return new Date(`${_}`)
    })
    .optional(),
  gender: z.enum(UserGenderType).optional(),
  mobile: z.string().optional(),
  ahv: z.string().optional(),
}
export const UserSalaryAttributesSchema = {
  year: z.string(),
  hasGA: z.boolean(),
  hasVerpflegung: z.boolean(),
  kinderzulagen: z.string(),
  ktg: z.string(),
  bvgAn: z.string(),
  bvgAg: z.string(),
  bvgCorrection: z.string().optional().nullable(),
  workload: z.string(),
  salaries: z.string().array(),
  lohnausweis: z.string().optional(),
}
export const UserContractAttributesSchema = {
  ends_at: z.string().optional(),
  description: z.string(),
}

export type UserProfileAttributesType = z.infer<z.ZodObject<typeof UserProfileAttributesSchema>>
export type UserSalaryAttributesType = z.infer<z.ZodObject<typeof UserSalaryAttributesSchema>>
export type UserContractAttributesType = z.infer<z.ZodObject<typeof UserContractAttributesSchema>>

/** A member as the menus, badges and owner columns show them. */
export type UserBasic = Pick<ApiUserOwner, 'id' | 'firstname' | 'lastname' | 'email' | 'picture'>

/** `GET /users`: a member as a list shows them, with no personal data. */
export type ApiUserSummary = ApiUserOwner & {
  openForWork: string | null
  linkedin: string | null
  /** Only with `withAccounts`. */
  accounts?: { id: number; type: AccountTypeValue }[] | null
}

/** `GET /users/{id}`: the user themselves, or anyone for an accountant. */
export type ApiUser = {
  id: number
  firstname: string
  lastname: string
  email: string
  roles: UserRolesTypeValue[]
  picture: string | null
  entered: string
  exited: string | null
  profile: UserProfileAttributesType
  salary: { configurations: UserSalaryAttributesType[] }
  contract: UserContractAttributesType
  resumes: ResumesType | null
  created: string
  updated: string
}

/** A user as the pages and forms render it: dates parsed, the year's balance attached. */
export type UserRow = Omit<ApiUser, 'entered' | 'exited' | 'created' | 'updated'> & {
  entered: Date
  exited: Date | null
  balancesheets?: Balancesheet[]
}

export const toUserRow = (user: ApiUser, balancesheets?: Balancesheet[]): UserRow => ({
  ...user,
  entered: new Date(user.entered),
  exited: user.exited ? new Date(user.exited) : null,
  balancesheets,
})

/** `PATCH /users/{id}`: one section at a time; all but `profile` are an accountant's. */
export type UserUpdate = {
  profile?: UserProfileAttributesType
  salaryConfiguration?: UserSalaryAttributesType
  contract?: { description?: string; ends_at?: string }
  /** `YYYY-MM-DD`. */
  entered?: string
  exited?: string | null
}

/** `GET /session`. */
export type ApiSession = {
  id: number
  email: string
  firstname: string
  lastname: string
  picture: string | null
  roles: UserRolesTypeValue[]
  isService: boolean
}

/** The session user with the display `name` the menus and avatars read. */
export const toSessionUser = (session: ApiSession) => ({
  ...session,
  name: `${session.firstname} ${session.lastname}`,
  image: session.picture,
})

type Tone = 'good' | 'warning' | 'critical'

/** `GET /users/overview`: one member of a year. */
export type ApiMemberOverviewRow = {
  userId: number
  firstname: string
  lastname: string
  email: string
  picture: string | null
  type: 'member' | 'aspirant'
  active: boolean
  monthly: { month: number; total: number }[]
  leistungenTotal: number
  membersaldo: number
  membersaldoTone: Tone
  ruecklagen: number
  ruecklagenByYear: { year: number; total: number }[]
  monatslohn: { amount: number; source: 'uniform' | 'flexible' | 'all_zero' | 'none'; monthly: number[] }
  avgRevenue: number
  /** `months` is `null` when the saldo never runs out. */
  forecast: { months: number | null; isInfinite: boolean; burn: number }
  forecastDate: { year: number; month: number } | null
  forecastTone: Tone
  forecastSalary: number
  forecastWindowMonths: number[]
  referenceYearMonth: { year: number; month: number }
  unpaidLeave: boolean
  workloadPercent: number
  upToMonth: number
}

/** `GET /users/{id}/resume`: the resume and what the template fills in about its user (#709). */
export type ApiUserResume = {
  id: number
  firstname: string
  lastname: string
  email: string
  profile: { github: string | null; linkedin: string | null; gender: string | null; birthday: string | null }
  /** `null` until the user saves a resume; the editor then starts from its template. */
  resumes: ResumesType | null
}
